import dayjs from "dayjs";
import { JobAction } from "~/interface/Job";

// History mirror — converts a SPA jobchain card into the legacy per-table history
// Order record that the mobile History screen reads from
//   customer/{storeId}/tables/{tableNumber}/history
// (HistoryFragmentV2 → Order.fromJSONObject, shows children with read == 2).
//
// Scope:
//   - cashier/terminal ADD (source !== 'm')           → isVoid:false
//   - VOID (cashier or mobile-initiated, SPA-created)  → isVoid:true (red -qty)
// Phone-originated adds write their own history record, so they're skipped to
// avoid duplicates. Everything else (move, pay, non-table modes) → null.
//
// IMPORTANT formatting facts (from Order.java / HistoryAdapterV2.java):
//   - the child VALUE must be a JSON *string* (HistoryFragment does
//     value.toString() → new JSONObject(...)), NOT a raw object.
//   - read must be 2 (shown in history AND skipped by the SPA's own ingest
//     watcher pos.mobile-v1.handleMobileV1, which early-returns on read == 2).
//   - datetime is REQUIRED and must be RFC822-offset (e.g. +0700, no colon) so
//     SimpleDateFormat "yyyy-MM-dd'T'HH:mm:ss.SSSZ" parses it; null datetime or
//     null username crashes the list adapter.
//   - item key names follow Constants.java: uniqueid / subitemid / name /
//     quantity / price / comments / toppings.
export interface HistoryRecordResult {
  tableNumber: number | null;
  record: any | null;
}

// Item builders are shared with AdapterMobileHistoryV2 (the alias-keyed
// mobile_v2_history node) so both silos emit byte-identical orderitems.
// Behaviour is unchanged — these were the private methods of this class.
export function buildVoidHistoryItems(job: any): any[] {
  const json2: Record<string, number> = job.json2 || {};
  const items = Array.isArray(job.__items) ? job.__items : [];
  return items.map((it: any) => {
    const qty = Math.abs(Number(json2[it.uuid] || 0)) || Number(it.quantity) || 0;
    return {
      uniqueid: String(it.uuid || ""),
      name: it.name || "",
      quantity: qty,
      subitemid: Number(it.subItemId || it.subitemid) || 0,
      price: Number(it.price) || 0,
      comments: it.comments || "",
      toppings: it.toppings || [],
    };
  });
}

export function buildAddHistoryItems(job: any): any[] {
  let order: any = {};
  try {
    order = typeof job.json === "string" ? JSON.parse(job.json) : job.json || {};
  } catch (e) {
    order = {};
  }
  const items = Array.isArray(order.orderitems) ? order.orderitems : [];
  return items.map((it: any) => ({
    uniqueid: String(it.uniqueid || it.uuid || ""),
    name: it.name || "",
    quantity: Number(it.quantity) || 0,
    subitemid: Number(it.subitemid || it.subItemId) || 0,
    price: Number(it.price) || 0,
    comments: it.comments || "",
    toppings: it.toppings || [],
  }));
}

export default class AdapterMobileHistoryV1 {
  fromJobToHistory(job: any): HistoryRecordResult {
    const empty: HistoryRecordResult = { tableNumber: null, record: null };
    if (!job) return empty;

    const isVoid = job.type === JobAction.VOID;
    const isAdd = job.type === JobAction.ADD;

    // Phone-originated adds already wrote their own history record — skip to
    // avoid duplicates. Voids are always SPA-created, so keep them.
    if (isAdd && job.source === "m") return empty;
    if (!isVoid && !isAdd) return empty;

    const tableNumber = usePosMobileV1Store().convertTableToUuid(job.alias);
    if (!tableNumber) return empty;

    const orderitems = isVoid ? this.buildVoidItems(job) : this.buildAddItems(job);
    if (orderitems.length === 0) return empty;

    const employeeName = job.employee?.name || "cashier";
    const datetime = dayjs(job.timestamp).format("YYYY-MM-DD[T]HH:mm:ss.SSSZZ");

    const record = {
      uniqueid: String(job.jobId || (process.client ? crypto.randomUUID() : Date.now())),
      username: employeeName,
      employeeName,
      employeeId: job.employee?.employeeId || 0,
      table: tableNumber,
      uuid: tableNumber,
      alias: job.alias || "",
      jobId: 0,
      customercount: Number(job.guests) || 0,
      datetime,
      orderitems,
      comments: job.note || job.__reason || "",
      read: 2,
      isVoid,
      handshake: true,
    };

    return { tableNumber, record };
  }

  private buildVoidItems(job: any): any[] {
    return buildVoidHistoryItems(job);
  }

  private buildAddItems(job: any): any[] {
    return buildAddHistoryItems(job);
  }
}
